import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { blogClient } from "../api/blogClient";
import "../../../styles/blog.css";

const copy = {
  pl: {
    eyebrow: "Blog Morfeusza",
    title: "Sny, symbole i nocne zapiski",
    searchPlaceholder: "Szukaj w blogu",
    search: "Szukaj",
    clear: "Wyczyść",
    open: "Otwórz",
    back: "Wróć do bloga",
    comments: "Komentarze",
    noComments: "Brak komentarzy.",
    commentPlaceholder: "Napisz komentarz...",
    addComment: "Dodaj komentarz",
    loginToComment: "Zaloguj się, aby dodać komentarz.",
    signIn: "Zaloguj",
    commentSending: "Wysyłam komentarz...",
    commentSaved: "Komentarz został dodany.",
    commentError: "Nie udało się dodać komentarza.",
    empty: "Brak wpisów.",
    loading: "Ładuję blog...",
    error: "Nie udało się pobrać bloga.",
    newer: "Nowsze",
    older: "Starsze",
    home: "Strona główna",
    room: "Pokoik",
    top: "Do góry",
  },
  en: {
    eyebrow: "Morfeusz blog",
    title: "Dreams, symbols and night notes",
    searchPlaceholder: "Search the blog",
    search: "Search",
    clear: "Clear",
    open: "Open",
    back: "Back to blog",
    comments: "Comments",
    noComments: "No comments yet.",
    commentPlaceholder: "Write a comment...",
    addComment: "Add comment",
    loginToComment: "Sign in to add a comment.",
    signIn: "Sign in",
    commentSending: "Sending comment...",
    commentSaved: "Comment added.",
    commentError: "Could not add the comment.",
    empty: "No posts found.",
    loading: "Loading blog...",
    error: "Could not load the blog.",
    newer: "Newer",
    older: "Older",
    home: "Home",
    room: "Room",
    top: "Back to top",
  },
};

const getInitialBlogState = () => {
  const hash = typeof window === "undefined" ? "#/blog" : window.location.hash || "#/blog";
  const normalized = hash.replace(/^#\/?/, "");
  const [path, query = ""] = normalized.split("?");
  const parts = path.split("/").filter(Boolean);
  const params = new URLSearchParams(query);

  return {
    slug: parts[0] === "blog" && parts[1] ? decodeURIComponent(parts[1]) : "",
    page: Math.max(1, Number(params.get("page") || 1) || 1),
    search: params.get("q") || "",
  };
};

const formatDate = (value, lang) => {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(value));
  } catch (_) {
    return "";
  }
};

const buildBlogHash = ({ slug = "", page = 1, search = "" } = {}) => {
  const base = slug ? `#/blog/${encodeURIComponent(slug)}` : "#/blog";
  const params = new URLSearchParams();
  if (!slug && page > 1) params.set("page", String(page));
  if (!slug && search.trim()) params.set("q", search.trim());
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

const scrollToTop = () => {
  if (typeof window === "undefined") return;
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const BlogPostCard = ({ post, lang, labels }) => (
  <article className="mdBlogCard" data-ui="blog-post-card">
    {post.image?.src ? (
      <a className="mdBlogCard__imageLink" href={buildBlogHash({ slug: post.slug })} aria-label={post.title}>
        <img className="mdBlogCard__image" src={post.image.src} alt={post.image.alt || post.title} loading="lazy" />
      </a>
    ) : null}
    <div className="mdBlogCard__body">
      <time className="mdBlogCard__date" dateTime={post.date}>{formatDate(post.date, lang)}</time>
      <h2 className="mdBlogCard__title"><a href={buildBlogHash({ slug: post.slug })}>{post.title}</a></h2>
      {post.excerpt ? <p className="mdBlogCard__excerpt">{post.excerpt}</p> : null}
      <a className="mdBtn mdBtn--secondary mdBlogCard__cta" href={buildBlogHash({ slug: post.slug })}>{labels.open}</a>
    </div>
  </article>
);

const BlogIndex = ({ lang, labels, state, setState }) => {
  const [posts, setPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [status, setStatus] = useState("loading");
  const [searchInput, setSearchInput] = useState(state.search || "");

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    blogClient.list({ page: state.page, search: state.search }).then((result) => {
      if (!alive) return;
      if (!result.ok) {
        setStatus("error");
        setPosts([]);
        setTotalPages(0);
        return;
      }
      setPosts(result.posts || []);
      setTotalPages(result.totalPages || 0);
      setStatus("ready");
    });
    return () => { alive = false; };
  }, [state.page, state.search]);

  useEffect(() => setSearchInput(state.search || ""), [state.search]);

  const submitSearch = (event) => {
    event.preventDefault();
    const next = { slug: "", page: 1, search: searchInput.trim() };
    window.location.hash = buildBlogHash(next);
    setState(next);
  };

  const clearSearch = () => {
    const next = { slug: "", page: 1, search: "" };
    window.location.hash = buildBlogHash(next);
    setState(next);
  };

  const goPage = (page) => {
    const next = { ...state, slug: "", page };
    window.location.hash = buildBlogHash(next);
    setState(next);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  return (
    <section className="mdBlogPage" data-ui="blog-index-page">
      <div className="mdBlogPage__inner">
        <header className="mdBlogHero">
          <p className="mdPage__eyebrow">{labels.eyebrow}</p>
          <h1 className="mdPage__title">{labels.title}</h1>
        </header>
        <form className="mdBlogSearch" onSubmit={submitSearch} data-ui="blog-search">
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={labels.searchPlaceholder} />
          <button type="submit" className="mdBtn mdBtn--primary">{labels.search}</button>
          {state.search ? <button type="button" className="mdBtn" onClick={clearSearch}>{labels.clear}</button> : null}
        </form>
        {status === "loading" ? <div className="mdCard mdBlogNotice">{labels.loading}</div> : null}
        {status === "error" ? <div className="mdCard mdBlogNotice">{labels.error}</div> : null}
        {status === "ready" && posts.length === 0 ? <div className="mdCard mdBlogNotice">{labels.empty}</div> : null}
        {posts.length ? <div className="mdBlogGrid">{posts.map((post) => <BlogPostCard key={post.id || post.slug} post={post} lang={lang} labels={labels} />)}</div> : null}
        {totalPages > 1 ? (
          <nav className="mdBlogPagination" aria-label="Blog pagination">
            <button type="button" className="mdBtn" disabled={state.page <= 1} onClick={() => goPage(state.page - 1)}>{labels.newer}</button>
            <span>{state.page} / {totalPages}</span>
            <button type="button" className="mdBtn" disabled={state.page >= totalPages} onClick={() => goPage(state.page + 1)}>{labels.older}</button>
          </nav>
        ) : null}
      </div>
    </section>
  );
};

const BlogComments = ({ post, labels, lang }) => {
  const { authReady, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [status, setStatus] = useState("loading");
  const [content, setContent] = useState("");
  const [submitStatus, setSubmitStatus] = useState("idle");

  useEffect(() => {
    if (!post?.id) return;
    let alive = true;
    setStatus("loading");
    blogClient.comments(post.id).then((result) => {
      if (!alive) return;
      if (!result.ok) {
        setComments([]);
        setStatus("error");
        return;
      }
      setComments(result.comments || []);
      setStatus("ready");
    });
    return () => { alive = false; };
  }, [post?.id]);

  const submitComment = async (event) => {
    event.preventDefault();
    const trimmed = content.trim();
    if (!trimmed || submitStatus === "sending") return;
    setSubmitStatus("sending");
    const result = await blogClient.addComment({ postId: post.id, content: trimmed });
    if (!result.ok) {
      setSubmitStatus("error");
      return;
    }
    setContent("");
    setSubmitStatus("saved");
    const refreshed = await blogClient.comments(post.id);
    if (refreshed.ok) setComments(refreshed.comments || []);
  };

  return (
    <section className="mdBlogComments" id="comments" data-ui="blog-comments">
      <header className="mdBlogComments__head">
        <p className="mdPage__eyebrow">{labels.comments}</p>
        <h2>{labels.comments}</h2>
      </header>
      {status === "loading" ? <div className="mdBlogCommentNotice">{labels.loading}</div> : null}
      {status === "error" ? <div className="mdBlogCommentNotice">{labels.error}</div> : null}
      {status === "ready" && comments.length === 0 ? <div className="mdBlogCommentNotice">{labels.noComments}</div> : null}
      {comments.length ? (
        <ol className="mdBlogCommentList">
          {comments.map((comment) => (
            <li className="mdBlogComment" key={comment.id}>
              <div className="mdBlogComment__meta">
                <strong>{comment.authorName}</strong>
                <time dateTime={comment.date}>{formatDate(comment.date, lang)}</time>
              </div>
              <p className="mdBlogComment__content">{comment.contentText}</p>
            </li>
          ))}
        </ol>
      ) : null}
      {authReady && isAuthenticated ? (
        <form className="mdBlogCommentForm" onSubmit={submitComment}>
          <textarea value={content} onChange={(event) => setContent(event.target.value)} placeholder={labels.commentPlaceholder} rows={5} />
          <div className="mdBlogCommentForm__row">
            <button type="submit" className="mdBtn mdBtn--primary" disabled={!content.trim() || submitStatus === "sending"}>{submitStatus === "sending" ? labels.commentSending : labels.addComment}</button>
            {submitStatus === "saved" ? <span>{labels.commentSaved}</span> : null}
            {submitStatus === "error" ? <span>{labels.commentError}</span> : null}
          </div>
        </form>
      ) : (
        <div className="mdBlogCommentLogin"><span>{labels.loginToComment}</span><a className="mdBtn" href="#/zaloguj">{labels.signIn}</a></div>
      )}
    </section>
  );
};

const BlogSingle = ({ lang, labels, slug, setState }) => {
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    blogClient.getBySlug(slug).then((result) => {
      if (!alive) return;
      if (!result.ok || !result.post) {
        setPost(null);
        setStatus("error");
        return;
      }
      setPost(result.post);
      setStatus("ready");
    });
    return () => { alive = false; };
  }, [slug]);

  const back = () => {
    const next = { slug: "", page: 1, search: "" };
    window.location.hash = buildBlogHash(next);
    setState(next);
  };

  return (
    <section className="mdBlogPage mdBlogPage--single" data-ui="blog-single-page">
      <div className="mdBlogPage__inner mdBlogPage__inner--single">
        {status === "loading" ? <div className="mdCard mdBlogNotice">{labels.loading}</div> : null}
        {status === "error" ? <div className="mdCard mdBlogNotice">{labels.error}</div> : null}
        {status === "ready" && post ? (
          <>
            <article className="mdBlogArticle">
              <header className="mdBlogArticle__head">
                <p className="mdPage__eyebrow">{labels.eyebrow}</p>
                <h1 className="mdPage__title">{post.title}</h1>
                <time className="mdBlogCard__date" dateTime={post.date}>{formatDate(post.date, lang)}</time>
              </header>
              {post.image?.src ? <img className="mdBlogArticle__image" src={post.image.src} alt={post.image.alt || post.title} /> : null}
              <div className="mdBlogArticle__content" dangerouslySetInnerHTML={{ __html: post.content }} />
            </article>
            <BlogComments post={post} labels={labels} lang={lang} />
            <nav className="mdBlogArticleActions" aria-label="Blog article actions">
              <button type="button" className="mdBtn" onClick={back}>{labels.back}</button>
              <a className="mdBtn" href="#top">{labels.home}</a>
              <a className="mdBtn mdBtn--primary" href="#/pokoik">{labels.room}</a>
            </nav>
          </>
        ) : null}
      </div>
      {status === "ready" && post ? <button type="button" className="mdBlogFloatingTop" onClick={scrollToTop} aria-label={labels.top}>↑</button> : null}
    </section>
  );
};

export const BlogPage = ({ lang = "pl" }) => {
  const [state, setState] = useState(getInitialBlogState);
  const labels = useMemo(() => copy[lang] || copy.pl, [lang]);

  useEffect(() => {
    const sync = () => setState(getInitialBlogState());
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  if (state.slug) return <BlogSingle lang={lang} labels={labels} slug={state.slug} setState={setState} />;
  return <BlogIndex lang={lang} labels={labels} state={state} setState={setState} />;
};
